import React, { useEffect, useRef } from 'react';
import { useDescope } from '@descope/react-sdk';
import { useTranslation } from 'localization';
import { PrimeReactButton as ButtonAtom } from 'design-system/my-lib';
import './OrganizationNotInEnvironmentScreen.scss';

export interface OrganizationNotInEnvironmentScreenProps {
  /** True when the person belongs to another organization in this environment. */
  hasOtherOrganizations: boolean;
  /** Enters another organization this person belongs to here. */
  onChooseAnotherOrganization: () => void;
}

/**
 * Shown when the session names an organization this environment does not have for the person
 * (`useAppInitialization`'s `not-in-environment` phase): they belong to it elsewhere, not here.
 * Trying again cannot change that, so there is no Try again. The way forward is another
 * organization they do belong to here (offered only when there is one), or signing out.
 */
const OrganizationNotInEnvironmentScreen: React.FC<OrganizationNotInEnvironmentScreenProps> = ({
  hasOtherOrganizations,
  onChooseAnotherOrganization,
}) => {
  const { t } = useTranslation();
  const { logout } = useDescope();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The whole view replaces the loader, so focus goes to the heading that names the new state.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <main className="org-not-in-environment-screen" data-testid="org-not-in-environment">
      <div className="org-not-in-environment-screen__panel">
        <h1 ref={headingRef} tabIndex={-1} className="org-not-in-environment-screen__heading">
          {t('appInit.notInEnvironment.title')}
        </h1>
        <p className="org-not-in-environment-screen__message">{t('appInit.notInEnvironment.description')}</p>
        <div className="org-not-in-environment-screen__actions">
          {hasOtherOrganizations && (
            <ButtonAtom
              label={t('appInit.notInEnvironment.chooseAnother')}
              data-testid="org-not-in-environment-choose-another"
              onClick={onChooseAnotherOrganization}
            />
          )}
          <ButtonAtom
            label={t('appInit.notInEnvironment.signOut')}
            variant={hasOtherOrganizations ? 'secondary' : undefined}
            data-testid="org-not-in-environment-sign-out"
            onClick={() => logout()}
          />
        </div>
      </div>
    </main>
  );
};

export default OrganizationNotInEnvironmentScreen;
